import { useState } from "react";
import { useCustom, type useTranslate } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

import { fetchCommMediaDataUri } from "../commMediaApi";
import { mediaTypeLabel } from "../commLabels";

// communicatie: "Afbeelding invoegen (mediabibliotheek)" — editor-gated picker. Lists media metadata
// (GET communicatie/media, RELATIVE), and on select fetches the chosen item's bytes AUTHENTICATED and
// inserts them as a self-contained base64 `data:` URI <img>. Rationale: a data: URI renders in the
// editor, the sandboxed preview, AND the server-side PDF (which already handles data: images) — a
// relative authed URL would NOT carry the bearer server-side.

type Translate = ReturnType<typeof useTranslate>;

interface CommMediaItem {
  id: string;
  name: string;
  fileName: string | null;
  contentType: string | null;
  mediaType: string | null;
  defaultWidthPx: number | null;
}

function isImage(contentType: string | null): boolean {
  return !!contentType && contentType.startsWith("image/");
}

export function MediaLibraryPicker({
  open,
  onClose,
  onInsert,
  t,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the data: URI + optional width to insert as an <img>. */
  onInsert: (dataUri: string, widthPx: number | null) => void;
  t: Translate;
}) {
  const { data, isLoading, isError } = useCustom<CommMediaItem[]>({
    url: "communicatie/media",
    method: "get",
    queryOptions: { enabled: open },
  });
  const rows = (data?.data ?? []).filter((r) => isImage(r.contentType));

  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(item: CommMediaItem) {
    setError(null);
    setBusyId(item.id);
    try {
      const dataUri = await fetchCommMediaDataUri(item.id);
      onInsert(dataUri, item.defaultWidthPx);
      onClose();
    } catch {
      setError(t("communicatieEditor.mediaInsert.error"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Modal
      open={open}
      title={t("communicatieEditor.mediaInsert.title")}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={!!busyId}>
          {t("common.cancel")}
        </Button>
      }
    >
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("communicatieRead.loadFailed")}</p>
      ) : rows.length === 0 ? (
        <p className="fawkes-muted">{t("communicatieEditor.mediaInsert.empty")}</p>
      ) : (
        <ul className="comm-editor__media-picker">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="comm-editor__media-picker-item"
                disabled={!!busyId}
                onClick={() => pick(r)}
              >
                <span className="comm-editor__media-picker-name">{r.name}</span>
                <span className="comm-editor__media-picker-meta">
                  {mediaTypeLabel(r.mediaType, t)}
                  {r.fileName ? ` · ${r.fileName}` : ""}
                  {busyId === r.id ? ` · ${t("common.loading")}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
